import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { TranslationService } from 'src/app/core/translation/translation.service';
import { ButtonType } from 'src/app/shared/common/components/buttons/button';
import { ContextMenuActions } from 'src/app/shared/common/components/context-menu/gsp-context-menu.component';
import { AccuracyUtils } from 'src/app/shared/common/utility/accuracy-utils';
import { GeneralUtils } from 'src/app/shared/common/utility/general-utils';
import { Feature } from 'src/app/shared/map-data-services/feature/feature';
import { MapWorkspacePermissionType } from 'src/app/shared/map-data-services/mapWorkspace/map-workspace-permission';

import { MessagingService } from 'src/app/core/messaging/messaging.service';
import { FeaturesStreamsService } from 'src/app/shared/common/current-features/features-streams.service';
import { TaskFeaturesStreamsService } from 'src/app/shared/common/current-features/task-features-streams.service';
import { ProjectStreamService } from 'src/app/shared/common/current-project/project-stream.service';
import { TasksStreamsService } from 'src/app/shared/common/current-tasks/tasks-streams.service';
import { MenuService } from 'src/app/shared/common/layout/menu.service';
import { LayerStyleGenerator } from 'src/app/shared/map-data-services/layer/layer-style-generator';
import { WorkspaceType } from 'src/app/shared/map-data-services/mapWorkspace/map-workspace.types';
import { ProjectTaskMigrationStatus } from 'src/app/shared/map-data-services/project/project';
import { Style } from 'src/app/shared/map-data-services/styles/style';
import { SymbologyOverrideChoice } from 'src/app/shared/map-data-services/styles/style-symbology';
import { ChartPanelStreamService } from '../../../common/chart-panel-stream.service';
import { MapMenuCode } from '../../../map-menus/map-menu-list';
import { PanelFeature } from '../feature-panel.component';

@Component({
    selector: 'feature-list',
    templateUrl: './feature-list.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FeatureListComponent implements OnChanges, OnDestroy, OnInit {
    @Input()
    selectedFeatures: Feature[] = [];

    @Input()
    activePanelFeature: PanelFeature = null;

    @Input()
    removeFromSelectionMode = false;

    @Input()
    workspaceType: WorkspaceType;

    @Output()
    setCurrentFeature = new EventEmitter<PanelFeature>();

    @Output()
    showDeleteAllConfirmation = new EventEmitter<void>();

    public contextMenuItems: ContextMenuActions[] = [
        {
            name: this.translateService.instant('TC.ToDos.AddToTask'),
            id: 'featurepanel-context-add',
            labelClass: 'featurepanel-context-menu-item',
            iconClass: 'icon_line_plus i24',
            divider: false,
            title: false,
            execute: $event => this.addFeaturesToTask(),
            visible: () => this.isTaskMigrationComplete
        },
        {
            name: this.translateService.instant('TC.ToDos.AddToNewTask'),
            id: 'featurepanel-context-newtask',
            labelClass: 'featurepanel-context-menu-item',
            iconClass: 'icon_solid_task_add_green_32 i24',
            divider: false,
            title: false,
            execute: $event => this.newTaskFromSelection(),
            visible: () => this.isTaskMigrationComplete
        },
        {
            name: this.translateService.instant('MapViewer_Generic_DeleteSelection'),
            iconClass: 'icon_solid_trash i24',
            id: 'delete-selected-features',
            divider: false,
            title: false,
            execute: () => this.showDeleteAllConfirmationAction(),
            visible: () => true
        }
    ];

    private readonly destroyed = new Subject<void>();
    isAccuracyPanelDisplayed$: Observable<boolean>;
    isLoadingFeatures$ = this.featuresStreams.isLoadingFeatures$;

    shouldShowAccuracyButton = false;
    shareMapUrl = false;
    isTaskMigrationComplete = false;

    // expose ButtonType enum to template
    public ButtonType = ButtonType;
    public MapWorkspacePermissionType = MapWorkspacePermissionType;
    public WorkspaceType = WorkspaceType;

    constructor(
        private projectStreamService: ProjectStreamService,
        private translateService: TranslationService,
        private chartPanelStreamService: ChartPanelStreamService,
        private menuService: MenuService,
        private tasksStreams: TasksStreamsService,
        private taskFeaturesStreams: TaskFeaturesStreamsService,
        private messaging: MessagingService,
        private router: Router,
        private featuresStreams: FeaturesStreamsService
    ) {
        this.isAccuracyPanelDisplayed$ = this.chartPanelStreamService.isAccuracyPanelDisplayedStream;
    }
    ngOnInit(): void {
        // taskMigrationStatusStream still keeps the status even on switching from GIS to other workspace.
        // So this.isTaskMigrationComplete will be false by default for other workspace types to hide the context menu items
        if (this.workspaceType === WorkspaceType.GIS) {
            this.isTaskMigrationComplete =
                this.projectStreamService.taskMigrationStatusStream.getValue() === ProjectTaskMigrationStatus.COMPLETED;
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.shareMapUrl = this.router.url.includes('shareMap');
        if (changes.selectedFeatures && changes.selectedFeatures.currentValue) {
            this.shouldShowAccuracyButton = !!this.selectedFeatures.find(
                feature =>
                    !GeneralUtils.isNullUndefinedOrNaN(
                        AccuracyUtils.getRoundedHorizontalAccuracyFromMetadata(feature.metadata, feature.geometryType)
                    )
            );
        }
    }

    ngOnDestroy(): void {
        this.destroyed.next(null);
    }

    private addFeaturesToTask() {
        this.tasksStreams.addFeaturesToTaskStream.next(
            this.selectedFeatures.filter(feature => feature.templateId || feature.templateSeriesId)
        );
        this.menuService.setActiveMenus([MapMenuCode.ADD_TO_TASK]);
    }

    private newTaskFromSelection() {
        this.taskFeaturesStreams.taskSelectedFeaturesStream.next(this.selectedFeatures);
        this.menuService.setActiveMenus([MapMenuCode.TASKS]);
    }

    setCurrentFeatureAction(feature: PanelFeature): void {
        this.setCurrentFeature.emit(feature);
    }

    private showDeleteAllConfirmationAction(): void {
        if (this.featuresStreams.isLoadingFeatures$.getValue()) {
            this.messaging.showWarning(this.translateService.instant('TC.MapViewer.ActionNotAllowed'));
            return;
        }

        this.showDeleteAllConfirmation.emit();
    }

    toggleAccuracyPanel(): void {
        this.chartPanelStreamService.isAccuracyPanelDisplayed = !this.chartPanelStreamService.isAccuracyPanelDisplayed;
    }

    public getFeatureFillColor(feature: Feature): string {
        if (feature.style?.styleOverrides?.length) {
            const overrideChoice: string = Object.keys(feature.properties).find(prop =>
                (feature.style.styleOverrides as SymbologyOverrideChoice[]).some(
                    override => override.choiceField === prop
                )
            );

            if (overrideChoice) {
                const override = (feature.style.styleOverrides as SymbologyOverrideChoice[]).find(
                    override =>
                        override.choiceField === overrideChoice &&
                        override.choiceValue === feature.properties[overrideChoice]
                );
                if (override) {
                    return LayerStyleGenerator.getFillColorFromOverride(
                        override,
                        feature.style as Style,
                        feature.geometryType,
                        feature.colorKey
                    );
                }
            }
        }

        return '#' + feature.colorKey;
    }

    public getFeatureOutlineColor(feature: Feature): string {
        if (feature.style?.styleOverrides?.length) {
            const overrideChoice: string = Object.keys(feature.properties).find(prop =>
                (feature.style.styleOverrides as SymbologyOverrideChoice[]).some(
                    override => override.choiceField === prop
                )
            );

            if (overrideChoice) {
                const override = (feature.style.styleOverrides as SymbologyOverrideChoice[]).find(
                    override =>
                        override.choiceField === overrideChoice &&
                        override.choiceValue === feature.properties[overrideChoice]
                );
                if (override) {
                    return LayerStyleGenerator.getOutlineColorFromOverride(
                        override,
                        feature.geometryType,
                        feature.colorKey
                    );
                }
            }
        }

        return LayerStyleGenerator.getOutlineColorFromStyle(
            feature.style as Style,
            feature.geometryType,
            feature.colorKey
        );
    }
}
